import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const OrdersPage = ({ orders, loadOrders, updateOrderStatus, cancelOrder }) => {
  const navigate = useNavigate();
  const FLOW = ['PLACED', 'PACKED', 'SHIPPED', 'DELIVERED'];
  const STATUS_FILTERS = ['all', ...FLOW, 'CANCELLED'];
  const [status, setStatus] = useState('all');
  const [phone, setPhone] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cancelDraft, setCancelDraft] = useState({ orderId: '', reason: '', error: '', saving: false });

  const formatDate = (value) => {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '-';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const nextStatus = (current) => {
    const idx = FLOW.indexOf(current);
    if (idx === -1) return null;
    return FLOW[Math.min(idx + 1, FLOW.length - 1)];
  };

  const openCancelBox = (orderId) => {
    setCancelDraft({ orderId, reason: '', error: '', saving: false });
  };

  const closeCancelBox = () => {
    setCancelDraft({ orderId: '', reason: '', error: '', saving: false });
  };

  const submitCancel = async (orderId) => {
    const reason = String(cancelDraft.reason || '').trim();
    if (reason.length < 3) {
      setCancelDraft((prev) => ({ ...prev, error: 'Please enter a valid cancellation reason.' }));
      return;
    }
    if (typeof cancelOrder !== 'function') return;
    try {
      setCancelDraft((prev) => ({ ...prev, saving: true, error: '' }));
      await cancelOrder(orderId, reason);
      closeCancelBox();
    } catch (e) {
      setCancelDraft((prev) => ({ ...prev, saving: false, error: e.message || 'Failed to cancel order' }));
    }
  };

  const getItemCount = (order) =>
    (order?.items || []).reduce((sum, item) => {
      const directQty = Number(item?.quantity || item?.pack_quantity || 0);
      if (directQty > 0) return sum + directQty;
      if (Array.isArray(item?.products) && item.products.length) {
        const nestedQty = item.products.reduce(
          (qtySum, p) => qtySum + Number(p?.total_quantity || p?.quantity_per_combo || 0),
          0
        );
        if (nestedQty > 0) return sum + nestedQty;
      }
      return sum + 1;
    }, 0);

  useEffect(() => {
    setPage(1);
  }, [status, phone, from, to, pageSize]);

  const filtered = useMemo(() => {
    const q = phone.trim().toLowerCase();
    return (orders || []).filter((o) => {
      const currentStatus = String(o.status || '').toUpperCase();
      if (status !== 'all' && currentStatus !== status) return false;
      if (q) {
        const ph = String(o.user_phone || '').toLowerCase();
        if (!ph.includes(q)) return false;
      }
      if (from) {
        const d = new Date(o.created_at);
        if (Number.isFinite(d.getTime())) {
          const start = new Date(from);
          if (d < start) return false;
        }
      }
      if (to) {
        const d = new Date(o.created_at);
        if (Number.isFinite(d.getTime())) {
          const end = new Date(to);
          end.setHours(23, 59, 59, 999);
          if (d > end) return false;
        }
      }
      return true;
    });
  }, [orders, status, phone, from, to]);

  const stats = useMemo(() => {
    const base = {
      all: (orders || []).length,
      PLACED: 0,
      PACKED: 0,
      SHIPPED: 0,
      DELIVERED: 0,
      CANCELLED: 0,
    };
    (orders || []).forEach((o) => {
      const s = String(o.status || '').toUpperCase();
      if (Object.prototype.hasOwnProperty.call(base, s)) {
        base[s] += 1;
      }
    });
    return base;
  }, [orders]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="card orders-page">
      <div className="orders-head">
        <div>
          <h3>Shipping Control</h3>
          <p className="muted">Track every order and push it to the next shipping stage quickly.</p>
        </div>
        <button className="secondary" onClick={loadOrders}>Refresh</button>
      </div>

      <div className="orders-kpi-grid">
        {STATUS_FILTERS.map((item) => {
          const key = item === 'all' ? 'all' : item;
          const count = stats[key] || 0;
          return (
            <button
              key={`kpi-${item}`}
              type="button"
              className={`orders-kpi-card ${status === item ? 'active' : ''}`}
              onClick={() => setStatus(item)}
            >
              <span className="orders-kpi-label">{item === 'all' ? 'All Orders' : item}</span>
              <span className="orders-kpi-value">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="filters">
        <div className="filter-row">
          <div>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'All' : s}</option>
              ))}
            </select>
          </div>
          <div>
            <label>User Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Search phone" />
          </div>
          <div>
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label>Page Size</label>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div>
            <label>Actions</label>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setStatus('all');
                setPhone('');
                setFrom('');
                setTo('');
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className="filter-actions">
          <div className="muted">Showing {paged.length} of {filtered.length}</div>
          <div className="pager">
            <button className="secondary" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span className="pager-info">Page {currentPage} of {totalPages}</span>
            <button className="secondary" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      </div>
      <div className="list">
        {paged.map((o) => {
          const currentStatus = String(o.status || '').toUpperCase();
          const canMove = FLOW.includes(currentStatus) && currentStatus !== 'DELIVERED';
          const canCancel = currentStatus !== 'CANCELLED' && currentStatus !== 'DELIVERED';
          const isCancelOpen = cancelDraft.orderId === o._id;
          return (
            <div key={o._id} className="list-item order-shipping-card">
            <div className="order-ship-head">
              <div>
                <div className="order-ship-id-row">
                  <span className={`badge-${String(o.status || '').toLowerCase()} order-ship-status`}>
                    {currentStatus}
                  </span>
                  <strong className="order-ship-id">#{String(o._id || '').slice(-8).toUpperCase()}</strong>
                </div>
                <div className="muted order-ship-full-id">{o._id}</div>
              </div>
              <div className="order-ship-amount">
                {'\u20B9'}{Number(o.total_amount || 0).toLocaleString('en-IN')}
              </div>
            </div>

            <div className="order-ship-meta">
              <span><b>Placed:</b> {formatDate(o.created_at)}</span>
              <span><b>Phone:</b> {o.user_phone || '-'}</span>
              <span><b>Items:</b> {getItemCount(o)}</span>
              <span><b>Delivery:</b> {'\u20B9'}{Number(o.delivery_charge || 0).toLocaleString('en-IN')}</span>
            </div>

            {currentStatus !== 'CANCELLED' ? (
              <div className="order-ship-progress-block">
                <div className="order-ship-progress">
                  <div
                    className="order-ship-progress-fill"
                    style={{
                      width: `${Math.max(
                        0,
                        (FLOW.indexOf(currentStatus) / (FLOW.length - 1)) * 100
                      )}%`,
                    }}
                  />
                </div>

                <div className="order-ship-step-row">
                  {FLOW.map((step) => {
                    const stepIndex = FLOW.indexOf(step);
                    const currentIndex = FLOW.indexOf(currentStatus);
                    return (
                      <div
                        key={`${o._id}-${step}`}
                        className={`order-ship-step ${
                          stepIndex < currentIndex ? 'done' : stepIndex === currentIndex ? 'active' : ''
                        }`}
                      >
                        <span className="order-ship-dot" />
                        <span>{step}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="order-ship-cancelled">
                This order is cancelled and cannot move to shipping.
                {o.cancellation_reason ? (
                  <div className="order-cancel-reason-text">Reason: {o.cancellation_reason}</div>
                ) : null}
              </div>
            )}

            <div className="row order-ship-actions">
              <button className="secondary" onClick={() => navigate(`/admin/orders/${o._id}`)}>
                View Items
              </button>
              {canMove ? (
                <button onClick={() => updateOrderStatus(o._id, nextStatus(currentStatus))}>
                  Mark as {nextStatus(currentStatus)}
                </button>
                ) : (
                  <span className="muted">
                    {currentStatus === 'DELIVERED'
                      ? 'Order delivered'
                      : 'No further action'}
                  </span>
                )}
              {canCancel ? (
                <button
                  type="button"
                  className="secondary order-cancel-trigger"
                  onClick={() => {
                    if (isCancelOpen) closeCancelBox();
                    else openCancelBox(o._id);
                  }}
                >
                  Cancel Order
                </button>
              ) : null}
            </div>
            {isCancelOpen ? (
              <div className="order-cancel-box">
                <label>Cancellation Reason</label>
                <textarea
                  value={cancelDraft.reason}
                  onChange={(e) => setCancelDraft((prev) => ({ ...prev, reason: e.target.value, error: '' }))}
                  placeholder="Add reason for cancellation (required)"
                />
                {cancelDraft.error ? <div className="error">{cancelDraft.error}</div> : null}
                <div className="row order-cancel-actions">
                  <button
                    type="button"
                    className="order-cancel-confirm"
                    disabled={cancelDraft.saving}
                    onClick={() => submitCancel(o._id)}
                  >
                    {cancelDraft.saving ? 'Cancelling...' : 'Confirm Cancel'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={cancelDraft.saving}
                    onClick={closeCancelBox}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrdersPage;
